import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { clientAPI, proposalAPI } from '../../services/api';

const GenerateProposal = () => {
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    clientId: '',
    projectTitle: '',
    requirements: ''
  });
  const [generatedProposal, setGeneratedProposal] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getAll();
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setGenerating(true);

    try {
      const response = await proposalAPI.generate({
        clientId: formData.clientId,
        projectTitle: formData.projectTitle,
        requirements: formData.requirements
      });
      
      setGeneratedProposal(response.data);
      setShowPreview(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate proposal');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await proposalAPI.save({
        ...generatedProposal,
        estimatedCost: generatedProposal.estimatedCost || 0
      });
      navigate('/proposals');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save proposal');
      setLoading(false);
    }
  };

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-0">
            <i className="bi bi-file-earmark-text me-2"></i>
            Generate AI Proposal
          </h2>
        </Col>
      </Row>

      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form onSubmit={handleGenerate}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Client *</Form.Label>
                  <Form.Select
                    name="clientId"
                    value={formData.clientId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Choose a client...</option>
                    {clients.map((client) => (
                      <option key={client._id} value={client._id}>
                        {client.clientName} - {client.companyName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Project Title *</Form.Label>
                  <Form.Control
                    type="text"
                    name="projectTitle"
                    value={formData.projectTitle}
                    onChange={handleChange}
                    placeholder="Enter project title"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Project Requirements *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    placeholder="Describe the project requirements in detail..."
                    required
                  />
                  <Form.Text className="text-muted">
                    Be as detailed as possible for better AI-generated content
                  </Form.Text>
                </Form.Group>

                <Button 
                  variant="primary" 
                  type="submit" 
                  disabled={generating}
                >
                  {generating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-magic me-2"></i>
                      Generate Proposal
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Generated Proposal Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="preview-content">
          {generatedProposal && (
            <div>
              <h4>{generatedProposal.projectTitle}</h4>
              <hr />
              
              <h5>Project Overview</h5>
              <p>{generatedProposal.generatedProposal?.overview}</p>
              
              <h5>Scope of Work</h5>
              <p>{generatedProposal.generatedProposal?.scopeOfWork}</p>
              
              <h5>Key Features</h5>
              <ul>
                {generatedProposal.generatedProposal?.features?.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
              
              <h5>Deliverables</h5>
              <ul>
                {generatedProposal.generatedProposal?.deliverables?.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              
              <h5>Timeline</h5>
              <p>{generatedProposal.generatedProposal?.timeline}</p>
              
              <h5>Assumptions</h5>
              <p>{generatedProposal.generatedProposal?.assumptions}</p>
              
              <h5>Terms & Conditions</h5>
              <p>{generatedProposal.generatedProposal?.termsAndConditions}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Edit
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Proposal'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GenerateProposal;
